import React, { useState } from 'react';
import NavigationBar from '@/components/ui/NavigationBar';
import Icon from '@/components/AppIcon';
import Button from '@/components/ui/Button';
import { useAuth } from '@/contexts/AuthContext';
import { useSubscription } from '@/hooks/useSubscription';
import { hasFeatureAccess } from '@/config/featureAccess';
import { NovaChatProvider } from '@/contexts/NovaChatContext';
import NovaChat from '@/components/NovaChat';
import UGCLayout from '@/components/layouts/UGCLayout';

const UGCHub = () => {
  const { profile } = useAuth();
  const { ugcTier } = useSubscription();
  const userTier = ugcTier || profile?.ugc_tier || 'free';
  const canAccess = hasFeatureAccess('app/ugc-dashboard', userTier);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [selectedPlatform, setSelectedPlatform] = useState('all');

  const ugcNavItems = [
    { path: '/app/ugc-hub', label: 'Creator Hub', icon: 'Compass' },
    { path: '/app/ugc-brands', label: 'Brands', icon: 'Database' },
    { path: '/app/ugc-crm', label: 'CRM', icon: 'Users' },
    { path: '/app/ugc-community', label: 'Community', icon: 'MessageCircle' },
    { path: '/app/ugc-scripts', label: 'Scripts', icon: 'FileText' },
    { path: '/app/teleprompter', label: 'Teleprompter', icon: 'PlayCircle' },
  ];

  const trendingNiches = [
    { category: 'Beauty', title: 'Glass Hair Routines', engagement: '+45%', icon: 'Sparkles', color: 'pink' },
    { category: 'Wellness', title: 'Sleep Supplements', engagement: '+38%', icon: 'Moon', color: 'purple' },
    { category: 'Fitness', title: 'Glute Day Hacks', engagement: '+42%', icon: 'Activity', color: 'blue' },
    { category: 'Tech', title: 'AI Productivity Tools', engagement: '+51%', icon: 'Zap', color: 'yellow' },
    { category: 'Home', title: 'Organization Hacks', engagement: '+35%', icon: 'Home', color: 'green' },
    { category: 'Food', title: 'Protein Snacks', engagement: '+40%', icon: 'Coffee', color: 'orange' }
  ];

  const platformInsights = [
    {
      platform: 'TikTok',
      icon: 'Music',
      color: 'cyan',
      specs: { duration: '9-15s', format: 'Vertical 9:16', hook: '<1s' },
      tips: ['Use trending audio at low volume', 'Text overlays for captions', 'Bright natural lighting', 'Quick cuts every 2-3s'],
      trending: ['#TikTokMadeMeBuyIt', '#GlowUp', '#MorningRoutine']
    },
    {
      platform: 'Instagram Reels',
      icon: 'Instagram',
      color: 'pink',
      specs: { duration: '15-30s', format: 'Vertical 9:16', hook: '<1s' },
      tips: ['Safe zones for UI elements', 'Hook in first second', 'Trending audio crucial', 'End with strong CTA'],
      trending: ['#ReelsInstagram', '#IGReels', '#ContentCreator']
    },
    {
      platform: 'YouTube Shorts',
      icon: 'Youtube',
      color: 'red',
      specs: { duration: '30-45s', format: 'Vertical 9:16', hook: '<2s' },
      tips: ['VO clarity is critical', 'End-card CTA required', 'Longer form = more watch time', 'Thumbnails still matter'],
      trending: ['#Shorts', '#YouTubeShorts', '#ContentCreation']
    }
  ];

  const curatedHooks = [
    { hook: 'POV: You need this before your next scroll ends', category: 'Urgency', performance: '8.2/10' },
    { hook: 'Creators: I used this to boost engagement +27%', category: 'Social Proof', performance: '9.1/10' },
    { hook: 'Stop wasting money on dupes—try this instead', category: 'Value', performance: '8.8/10' },
    { hook: 'I tested 50 products. Only these 3 worked', category: 'Authority', performance: '9.4/10' },
    { hook: 'Watch before you buy [product name]', category: 'Warning', performance: '8.5/10' },
    { hook: 'Why is no one talking about this?', category: 'Curiosity', performance: '7.9/10' }
  ];

  const creatorTools = [
    { name: 'Script Generator', description: 'AI-powered scripts for any niche', icon: 'FileText', link: '/app/ugc-scripts' },
    { name: 'Teleprompter', description: 'Record with on-screen prompts', icon: 'Video', link: '/app/teleprompter' },
    { name: 'Brand Database', description: 'Track outreach and partnerships', icon: 'Database', link: '/app/ugc-brands' },
    { name: 'Community Feedback', description: 'Get peer reviews on content', icon: 'MessageSquare', link: '/app/ugc-community' }
  ];

  const categories = ['all', 'Beauty', 'Wellness', 'Fitness', 'Tech', 'Home', 'Food'];
  const platforms = ['all', 'TikTok', 'Instagram Reels', 'YouTube Shorts'];

  const filteredNiches = selectedCategory === 'all' 
    ? trendingNiches 
    : trendingNiches.filter(n => n.category === selectedCategory);

  const filteredPlatforms = selectedPlatform === 'all'
    ? platformInsights
    : platformInsights.filter(p => p.platform === selectedPlatform);

  const getBgColorClass = (color) => {
    const colors = {
      pink: 'bg-pink-500/10 border-pink-500/20',
      purple: 'bg-purple-500/10 border-purple-500/20',
      blue: 'bg-blue-500/10 border-blue-500/20',
      yellow: 'bg-yellow-500/10 border-yellow-500/20',
      green: 'bg-green-500/10 border-green-500/20',
      orange: 'bg-orange-500/10 border-orange-500/20',
      cyan: 'bg-cyan-500/10 border-cyan-500/20',
      red: 'bg-red-500/10 border-red-500/20'
    };
    return colors[color] || colors.blue;
  };

  const getIconColorClass = (color) => {
    const colors = {
      pink: 'text-pink-600',
      purple: 'text-purple-600',
      blue: 'text-blue-600',
      yellow: 'text-yellow-600',
      green: 'text-green-600',
      orange: 'text-orange-600',
      cyan: 'text-cyan-600',
      red: 'text-red-600'
    };
    return colors[color] || colors.blue;
  };

  return (
    <UGCLayout className="pb-20">
      <NavigationBar items={ugcNavItems} hideMore hideLocks homePath="/app/ugc-dashboard" />
      <main className="max-w-7xl mx-auto px-4 py-10">
        {/* Header */}
        <div className="mb-8">
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-amber-500/20 border border-amber-500/40 mb-4">
            <Icon name="Compass" size={16} color="#FCD34D" />
            <span className="text-xs font-semibold text-amber-100">Creator Hub</span>
          </div>
          <h1 className="text-4xl font-bold text-white mb-3">What's Hot Right Now</h1>
          <p className="text-amber-100/80 max-w-3xl text-lg">
            Trending niches, platform insights, and high-performing hooks. Use these to shape scripts, shot lists, and brand outreach.
          </p>
        </div>

        {!canAccess && (
          <div className="rounded-2xl border border-white/10 bg-slate-800/40 p-8 shadow-lg">
            <Icon name="Lock" size={32} className="text-amber-300 mb-3" />
            <h3 className="text-xl font-semibold text-white mb-2">UGC Creator Access Required</h3>
            <p className="text-sm text-amber-100/80 mb-4">Unlock trending insights, platform analytics, and creator tools.</p>
            <Button onClick={() => (window.location.href = '/ugc-subscription')} className="bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 text-white shadow-lg">
              View UGC Plans
            </Button>
          </div>
        )}

        {canAccess && (
          <div className="space-y-8">
            {/* Quick Stats */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="rounded-xl border border-border bg-gradient-to-br from-blue-50 to-white p-4 shadow-sm">
                <Icon name="TrendingUp" size={20} className="text-blue-600 mb-2" />
                <p className="text-2xl font-bold text-foreground">24</p>
                <p className="text-xs text-muted-foreground">Trending Niches</p>
              </div>
              <div className="rounded-xl border border-border bg-gradient-to-br from-purple-50 to-white p-4 shadow-sm">
                <Icon name="Target" size={20} className="text-purple-600 mb-2" />
                <p className="text-2xl font-bold text-foreground">150+</p>
                <p className="text-xs text-muted-foreground">Hook Templates</p>
              </div>
              <div className="rounded-xl border border-border bg-gradient-to-br from-green-50 to-white p-4 shadow-sm">
                <Icon name="Users" size={20} className="text-green-600 mb-2" />
                <p className="text-2xl font-bold text-foreground">3.2k</p>
                <p className="text-xs text-muted-foreground">Active Creators</p>
              </div>
              <div className="rounded-xl border border-border bg-gradient-to-br from-orange-50 to-white p-4 shadow-sm">
                <Icon name="Award" size={20} className="text-orange-600 mb-2" />
                <p className="text-2xl font-bold text-foreground">45%</p>
                <p className="text-xs text-muted-foreground">Avg. Engagement Boost</p>
              </div>
            </div>

            {/* Trending Niches */}
            <section>
              <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
                <h2 className="text-2xl font-bold text-white flex items-center gap-2">
                  <Icon name="Flame" size={24} className="text-orange-500" />
                  Trending Niches
                </h2>
                <div className="flex gap-2 flex-wrap">
                  {categories.map(cat => (
                    <button
                      key={cat}
                      onClick={() => setSelectedCategory(cat)}
                      className={`px-3 py-1 rounded-full text-xs font-medium transition-all ${
                        selectedCategory === cat
                          ? 'bg-amber-500 text-white shadow-md'
                          : 'bg-white/10 border border-white/10 text-amber-200/80 hover:bg-white/15'
                      }`}
                    >
                      {cat}
                    </button>
                  ))}
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {filteredNiches.map((niche, idx) => (
                  <div key={idx} className={`rounded-xl border p-5 shadow-sm hover:shadow-md transition-all ${getBgColorClass(niche.color)}`}>
                    <div className="flex items-start justify-between mb-3">
                      <Icon name={niche.icon} size={24} className={getIconColorClass(niche.color)} />
                      <span className="px-2 py-0.5 rounded-full bg-white/80 text-xs font-semibold text-foreground">
                        {niche.engagement}
                      </span>
                    </div>
                    <h3 className="text-lg font-semibold text-foreground mb-1">{niche.title}</h3>
                    <p className="text-xs text-muted-foreground mb-3">{niche.category}</p>
                    <Button size="xs" variant="outline" className="w-full">
                      Generate Script
                    </Button>
                  </div>
                ))}
              </div>
            </section>

            {/* Platform Insights */}
            <section>
              <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
                <h2 className="text-2xl font-bold text-white flex items-center gap-2">
                  <Icon name="Monitor" size={24} className="text-blue-500" />
                  Platform Insights
                </h2>
                <div className="flex gap-2">
                  {platforms.map(plat => (
                    <button
                      key={plat}
                      onClick={() => setSelectedPlatform(plat)}
                      className={`px-3 py-1 rounded-full text-xs font-medium transition-all ${
                        selectedPlatform === plat
                          ? 'bg-blue-500 text-white shadow-md'
                          : 'bg-white border border-border text-muted-foreground hover:border-blue-300'
                      }`}
                    >
                      {plat}
                    </button>
                  ))}
                </div>
              </div>
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                {filteredPlatforms.map((platform, idx) => (
                  <div key={idx} className={`rounded-xl border p-6 shadow-sm hover:shadow-lg transition-all ${getBgColorClass(platform.color)}`}>
                    <Icon name={platform.icon} size={32} className={`${getIconColorClass(platform.color)} mb-4`} />
                    <h3 className="text-xl font-bold text-white mb-4">{platform.platform}</h3>
                    
                    <div className="space-y-3 mb-4">
                      <div className="flex justify-between items-center p-2 rounded-lg bg-white/60">
                        <span className="text-xs text-muted-foreground">Duration</span>
                        <span className="text-xs font-semibold text-foreground">{platform.specs.duration}</span>
                      </div>
                      <div className="flex justify-between items-center p-2 rounded-lg bg-white/60">
                        <span className="text-xs text-muted-foreground">Format</span>
                        <span className="text-xs font-semibold text-foreground">{platform.specs.format}</span>
                      </div>
                      <div className="flex justify-between items-center p-2 rounded-lg bg-white/60">
                        <span className="text-xs text-muted-foreground">Hook Time</span>
                        <span className="text-xs font-semibold text-foreground">{platform.specs.hook}</span>
                      </div>
                    </div>

                    <div className="mb-4">
                      <p className="text-xs font-semibold text-white mb-2">Pro Tips:</p>
                      <ul className="space-y-1">
                        {platform.tips.map((tip, i) => (
                          <li key={i} className="text-xs text-amber-100/80 flex items-start gap-1">
                            <span className="text-green-600 mt-0.5">•</span>
                            {tip}
                          </li>
                        ))}
                      </ul>
                    </div>

                    <div>
                      <p className="text-xs font-semibold text-white mb-2">Trending Tags:</p>
                      <div className="flex flex-wrap gap-1">
                        {platform.trending.map((tag, i) => (
                          <span key={i} className="px-2 py-0.5 rounded-full bg-white/10 text-xs text-amber-100">
                            {tag}
                          </span>
                        ))}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </section>

            {/* Curated Hooks */}
            <section>
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-2xl font-bold text-white flex items-center gap-2">
                  <Icon name="Zap" size={24} className="text-yellow-500" />
                  High-Performance Hooks
                </h2>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {curatedHooks.map((hook, idx) => (
                  <div key={idx} className="rounded-xl border border-border bg-white p-5 shadow-sm hover:shadow-md transition-all">
                    <div className="flex items-start justify-between mb-3">
                      <span className="px-2 py-1 rounded-md bg-purple-100 text-purple-700 text-xs font-semibold">
                        {hook.category}
                      </span>
                      <div className="flex items-center gap-1">
                        <Icon name="Star" size={14} className="text-yellow-500" />
                        <span className="text-xs font-semibold text-white">{hook.performance}</span>
                      </div>
                    </div>
                    <p className="text-sm font-medium text-white mb-3">"{hook.hook}"</p>
                    <div className="flex gap-2">
                      <Button size="xs" variant="outline" className="flex-1">Copy Hook</Button>
                      <Button size="xs" className="flex-1 bg-purple-500 hover:bg-purple-600 text-white">Use in Script</Button>
                    </div>
                  </div>
                ))}
              </div>
            </section>

            {/* Creator Tools */}
            <section>
              <h2 className="text-2xl font-bold text-white mb-4 flex items-center gap-2">
                <Icon name="Tool" size={24} className="text-green-500" />
                Quick Access Tools
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                {creatorTools.map((tool, idx) => (
                  <div key={idx} className="rounded-xl border border-border bg-white p-5 shadow-sm hover:shadow-lg transition-all cursor-pointer"
                    onClick={() => window.location.href = tool.link}>
                    <Icon name={tool.icon} size={28} className="text-amber-600 mb-3" />
                    <h3 className="text-md font-semibold text-foreground mb-2">{tool.name}</h3>
                    <p className="text-xs text-muted-foreground mb-3">{tool.description}</p>
                    <div className="flex items-center gap-1 text-xs text-amber-600 font-medium">
                      Open Tool
                      <Icon name="ArrowRight" size={14} />
                    </div>
                  </div>
                ))}
              </div>
            </section>
          </div>
        )}
      </main>
      <NovaChat />
    </UGCLayout>
  );
};

const UGCHubWithProvider = () => (
  <NovaChatProvider>
    <UGCHub />
  </NovaChatProvider>
);

export default UGCHubWithProvider;
